// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { color, mergeWithCss } from "@adl/XDL/Utilities";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { PromotionModel } from "@insite/client-framework/Types/ApiModels";
import LocalizedCurrency from "@insite/content-library/Components/LocalizedCurrency";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import React, { Fragment } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    isLoading?: boolean;
    /**
     * If true, displays an empty cart total UI.
     * If false, displays amounts from the cart.
     * Default value: false
     */
    isCartEmpty?: boolean;
    /**
     * If true, displays tax, shipping and handling, and total amounts.
     * If false, does not display tax, shipping and handling, or total amounts.
     * Default value: true
     */
    showTaxAndShipping?: boolean;
    cart?: Cart;
    orderPromotions?: PromotionModel[];
    shippingPromotions?: PromotionModel[];
    discountTotal?: number;
    extendedStyles?: CartTotalDisplayStyles;
    // XNG-Changes:
    showQty?: boolean;
    showTotal?: boolean;
    showSeparators?: boolean;
    showTaxAndShippingInfoMessage?: boolean;
    showPst?: boolean;
    showGst?: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    showPricing: state.context.showPricing,
    isCheckoutReviewAndSubmitPage: getCurrentPage(state).type === "CheckoutReviewAndSubmitPage",
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface CartTotalDisplayStyles {
    container?: GridContainerProps;
    labelGridItem?: GridItemProps;
    valueGridItem?: GridItemProps;
    subtotalLabel?: TypographyProps;
    subtotalValue?: TypographyProps;
    promotionLabel?: TypographyProps;
    promotionValue?: TypographyProps;
    discountsLabel?: TypographyProps;
    discountsValue?: TypographyProps;
    shippingAndHandlingLabel?: TypographyProps;
    shippingAndHandlingValue?: TypographyProps;
    otherChargesLabel?: TypographyProps;
    otherChargesValue?: TypographyProps;
    taxLabel?: TypographyProps;
    taxValue?: TypographyProps;
    totalLabel?: TypographyProps;
    totalValue?: TypographyProps;
    loadingSpinner?: LoadingSpinnerProps;
    // XNG-Changes
    titleGridItem?: GridItemProps;
    titleText?: TypographyProps;
    separator?: GridItemProps;
    qtyLabel?: TypographyProps;
    qtyValue?: TypographyProps;
    taxAndShippingLabel?: TypographyProps;
    taxAndShippingValue?: TypographyProps;
    taxAndShippingMessage?: {
        gridItem?: GridItemProps;
        text?: TypographyProps;
    };
}
export const commonTextStyles = (customCss?: string): TypographyProps => ({
    size: 16,
    weight: "bold",
    lineHeight: "17px",
    css: css`
        letter-spacing: 0;
        ${customCss}
    `,
});

export const cartTotalDisplayStyles: CartTotalDisplayStyles = {
    container: {
        gap: 0,
        css: css`
            background-color: ${getColor("common.white")};
            padding: 0px;
        `,
        offsetCss: css`
            margin: 0;
            &&&&& {
                width: 100%;
            }
        `,
    },
    labelGridItem: {
        width: 6,
        css: css`
            padding: 12px 16px;
            @media print {
                padding: 4px 16px;
            }
        `,
    },
    valueGridItem: {
        width: 6,
        css: css`
            padding: 12px 16px;
            @media print {
                padding: 4px 16px;
            }
        `,
    },
    subtotalLabel: {
        ...commonTextStyles(),
    },
    subtotalValue: {
        ...commonTextStyles("margin-left: auto;"),
    },
    promotionValue: {
        css: css`
            margin-left: auto;
        `,
    },
    discountsValue: {
        css: css`
            margin-left: auto;
        `,
    },
    shippingAndHandlingValue: {
        css: css`
            margin-left: auto;
        `,
    },
    otherChargesValue: {
        css: css`
            margin-left: auto;
        `,
    },
    taxValue: {
        css: css`
            margin-left: auto;
        `,
    },
    totalLabel: {
        ...commonTextStyles(),
        size: 18,
        lineHeight: "22px",
    },
    totalValue: {
        ...commonTextStyles(`
            margin-left: auto;
        `),
        size: 20,
        lineHeight: "28px",
    },
    loadingSpinner: {
        size: 15,
        css: css`
            margin-top: auto;
            margin-bottom: auto;
            margin-left: auto;
        `,
    },
    titleGridItem: {
        width: 12,
        css: css`
            background: ${color("common.ui9")};
            padding: 12px 16px;
        `,
    },
    titleText: { ...commonTextStyles() },
    separator: {
        width: 12,
        css: css`
            border-top: 1px solid ${color("common.ui5")};
            &&& {
                margin: 0 16px;
            }
        `,
    },
    qtyLabel: {
        ...commonTextStyles(),
    },
    qtyValue: {
        ...commonTextStyles("margin-left: auto;"),
    },
    taxAndShippingLabel: {
        ...commonTextStyles(),
        size: 15,
        lineHeight: "26px",
    },
    taxAndShippingValue: {
        ...commonTextStyles("margin-left: auto;"),
        size: 15,
        lineHeight: "26px",
    },
    taxAndShippingMessage: {
        gridItem: {
            width: 12,
            css: css`
                padding: 12px 16px;
            `,
        },
        text: {
            size: 12,
            css: css`
                letter-spacing: 0;
                line-height: 18px;
            `,
        },
    },
};

const formatTaxValue = (val?: string) => {
    if (!val) {
        return "0.0$";
    }

    return val.includes("$") ? val : `$${val}`;
};

const CartTotalDisplay = ({
    isCartEmpty = false,
    showTaxAndShipping = false,
    cart,
    orderPromotions,
    shippingPromotions,
    discountTotal,
    isLoading,
    enableVat,
    showPricing,
    isCheckoutReviewAndSubmitPage,
    showTotal,
    showSeparators,
    showQty,
    showTaxAndShippingInfoMessage,
    extendedStyles,
    showPst,
    showGst,
}: Props) => {
    // XNG-Changes: change mergeToNew to mergeWithCss
    const [styles] = React.useState(() => mergeWithCss(cartTotalDisplayStyles, extendedStyles));

    if (!showPricing && !isCheckoutReviewAndSubmitPage) {
        return null;
    }

    if (isLoading || !cart || isCartEmpty) {
        return null;
    }

    const filteredOrderPromotions = orderPromotions?.filter(o => o.amount > 0);
    const filteredShippingPromotions = shippingPromotions?.filter(o => o.amount > 0);

    return (
        <GridContainer {...styles.container} data-test-selector={`cartTotal_${cart.id}`}>
            <GridItem {...styles.titleGridItem}>
                <Typography {...styles.titleText}>{translate("Order Summary")}</Typography>
            </GridItem>
            {showQty && (
                <>
                    <GridItem {...styles.labelGridItem}>
                        <Typography {...styles.qtyLabel}>{translate("Quantity")}</Typography>
                    </GridItem>
                    <GridItem {...styles.valueGridItem}>
                        <Typography {...styles.qtyValue} data-test-selector="cartTotal_qty">
                            {cart.totalQtyOrdered}
                        </Typography>
                    </GridItem>
                    <GridItem {...styles.separator} />
                </>
            )}

            <GridItem {...styles.labelGridItem}>
                <Typography {...styles.subtotalLabel}>
                    {translate("Subtotal") + (enableVat ? ` (${translate("Ex. VAT")})` : "")}
                </Typography>
            </GridItem>
            <GridItem {...styles.valueGridItem}>
                <Typography {...styles.subtotalValue} data-test-selector="cartTotal_subTotal">
                    {cart.orderSubTotalDisplay}
                </Typography>
            </GridItem>
            {showSeparators && <GridItem {...styles.separator} />}
            {filteredOrderPromotions &&
                filteredOrderPromotions.map(promotion => (
                    <Fragment key={promotion.id}>
                        <GridItem {...styles.labelGridItem}>
                            <Typography {...styles.promotionLabel} data-test-selector="cartTotal_orderPromotionName">
                                {`${translate("Promotion")}: ${promotion.name}`}
                            </Typography>
                        </GridItem>
                        <GridItem {...styles.valueGridItem}>
                            <Typography {...styles.promotionValue} data-test-selector="cartTotal_orderPromotionValue">
                                {`-${promotion.amountDisplay}`}
                            </Typography>
                        </GridItem>
                    </Fragment>
                ))}
            {/* <XNG-Changes> */}
            {/* {showTaxAndShipping && cart.shippingAndHandlingDisplay.length > 0 && (
                <>
                    <GridItem {...styles.labelGridItem}>
                        <Typography {...styles.taxAndShippingLabel}>{translate("Shipping & Handling")}</Typography>
                    </GridItem>
                    <GridItem {...styles.valueGridItem}>
                        <Typography
                            {...styles.taxAndShippingValue}
                            data-test-selector="cartTotal_shippingAndHandlingAmount"
                        >
                            {cart.shippingAndHandlingDisplay}
                        </Typography>
                    </GridItem>
                    {showSeparators && <GridItem {...styles.separator} />}
                </>
            )} */}

            {filteredShippingPromotions &&
                filteredShippingPromotions.map(promotion => (
                    <Fragment key={promotion.id}>
                        <GridItem {...styles.labelGridItem}>
                            <Typography {...styles.promotionLabel}>
                                {`${translate("Promotion")}: ${promotion.name}`}
                            </Typography>
                        </GridItem>
                        <GridItem {...styles.valueGridItem}>
                            <Typography
                                {...styles.promotionValue}
                                data-test-selector="cartTotal_shippingPromotionValue"
                            >
                                {`-${promotion.amountDisplay}`}
                            </Typography>
                        </GridItem>
                    </Fragment>
                ))}
            {showGst && cart.properties.gst && (
                <>
                    <GridItem {...styles.labelGridItem}>
                        <Typography {...styles.taxAndShippingLabel}>{translate("GST")}</Typography>
                    </GridItem>
                    <GridItem {...styles.valueGridItem}>
                        <Typography {...styles.taxAndShippingValue} data-test-selector="cartTotal_totalTaxDisplay">
                            {formatTaxValue(cart.properties.gst)}
                        </Typography>
                    </GridItem>
                </>
            )}
            {showPst && cart.properties.pst && (
                <>
                    <GridItem {...styles.labelGridItem}>
                        <Typography {...styles.taxAndShippingLabel}>{translate("PST")}</Typography>
                    </GridItem>
                    <GridItem {...styles.valueGridItem}>
                        <Typography {...styles.taxAndShippingValue} data-test-selector="cartTotal_totalTaxDisplay">
                            {formatTaxValue(cart.properties.pst)}
                        </Typography>
                    </GridItem>
                </>
            )}
            {showTaxAndShipping && cart.totalTaxDisplay.length > 0 && cart.customerOrderTaxes!.length === 0 && (
                <>
                    <GridItem {...styles.labelGridItem}>
                        <Typography {...styles.taxAndShippingLabel}>
                            {enableVat ? translate("VAT") : translate("Tax")}
                        </Typography>
                    </GridItem>
                    <GridItem {...styles.valueGridItem}>
                        <Typography {...styles.taxAndShippingValue} data-test-selector="cartTotal_totalTaxDisplay">
                            {cart.totalTaxDisplay}
                        </Typography>
                    </GridItem>
                    {showSeparators && <GridItem {...styles.separator} />}
                </>
            )}
            {showTaxAndShipping &&
                [...cart.customerOrderTaxes!]
                    .sort((a, b) => a.sortOrder - b.sortOrder)
                    .map(tax => {
                        const key = `${tax.taxCode}_${tax.sortOrder}`;
                        return (
                            <Fragment key={key}>
                                <GridItem {...styles.labelGridItem}>
                                    <Typography>{tax.taxDescription || translate("Tax")}</Typography>
                                </GridItem>
                                <GridItem {...styles.valueGridItem}>
                                    <Typography {...styles.taxValue}>{tax.taxAmountDisplay}</Typography>
                                </GridItem>
                            </Fragment>
                        );
                    })}
            {showTaxAndShippingInfoMessage && (
                <GridItem {...styles.taxAndShippingMessage?.gridItem}>
                    <Typography {...styles.taxAndShippingMessage?.text}>
                        {siteMessage("Custom_Shipping_Disclaimer")}
                    </Typography>
                </GridItem>
            )}
            {showTotal && (
                <>
                    <GridItem {...styles.separator} />
                    <GridItem {...styles.labelGridItem}>
                        <Typography {...styles.totalLabel}>{translate("Total")}</Typography>
                    </GridItem>
                    <GridItem {...styles.valueGridItem}>
                        <Typography {...styles.totalValue} data-test-selector="cartTotal_orderGrandTotalDisplay">
                            {cart.orderGrandTotalDisplay}
                        </Typography>
                    </GridItem>
                </>
            )}
            {discountTotal !== undefined && discountTotal > 0 && (
                <>
                    <GridItem {...styles.labelGridItem}>
                        <Typography {...styles.discountsLabel}>{translate("Discounts")}</Typography>
                    </GridItem>
                    <GridItem {...styles.valueGridItem}>
                        <Typography {...styles.discountsValue} data-test-selector="cartTotal_totalDiscounts">
                            {`${translate("You saved")} `}{" "}
                            <LocalizedCurrency amount={discountTotal} currencySymbol={cart.currencySymbol} />
                        </Typography>
                    </GridItem>
                </>
            )}
        </GridContainer>
    );
};

export default connect(mapStateToProps)(CartTotalDisplay);
